import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Chip,
  Button,
} from "@mui/material";

const DropdownWithSelectedList = ({
  label,
  name,
  value = [],
  onChange,
  options = [],
  error,
  required = false,
  disabled = false,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Normalize options (label/value)
  const formattedOptions = useMemo(
    () =>
      options.map(opt => ({
        label: opt.label || opt.name,
        value: opt.value || opt.id,
      })),
    [options]
  );

  // Match IDs to objects for display
  const selectedOptions = formattedOptions.filter(opt =>
    selectedIds.includes(opt.value)
  );

  // Hide selected items from dropdown
  const availableOptions = formattedOptions.filter(
    opt => !selectedIds.includes(opt.value)
  );

  // Handle select (keep dropdown open & clear input)
  const handleSelect = (event, newValue, reason) => {
    if (disabled) return;
    if (reason === "selectOption" && newValue && !selectedIds.includes(newValue.value)) {
      const updated = [...selectedIds, newValue.value];
      setSelectedIds(updated);
      setInputValue(""); // clear search input
      if (onChange)
        onChange({ target: { name, value: updated } });
    }
  };

  // Handle delete chip
  const handleChipDelete = idToRemove => {
    if (disabled) return;
    const updated = selectedIds.filter(id => id !== idToRemove);
    setSelectedIds(updated);
    if (onChange)
      onChange({ target: { name, value: updated } });
  };

  const toggleShowAll = () => setShowAll(prev => !prev);

  // Sync when editing
  useEffect(() => {
    if (Array.isArray(value) && value.length > 0) {
      const normalized = value.map(v =>
        typeof v === "string" ? parseInt(v) : v
      );
      setSelectedIds(normalized);
    } else {
      setSelectedIds([]);
    }
  }, [JSON.stringify(value), options.length]);

  return (
    <Box sx={{ width: "100%", mt: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
        {label} {required && <span style={{ color: "red" }}>*</span>}
      </Typography>

      <Autocomplete
        disabled={disabled}
        options={availableOptions}
        getOptionLabel={(option) => option.label || ""}
        onChange={handleSelect}
        value={null} // Keeps field empty after select
        inputValue={inputValue}
        onInputChange={(e, newVal, reason) => {
          if (reason !== "reset") setInputValue(newVal);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={`Search ${label.toLowerCase()}...`}
            error={!!error}
            helperText={error}
            size="small"
          />
        )}
        openOnFocus
        disableCloseOnSelect // ✅ keeps dropdown open after each selection
        sx={{
          "& .MuiAutocomplete-input": { fontSize: "0.9rem" },
        }}
      />

      {/* Selected list */}
      <Box sx={{ mt: 1 }}>
        {selectedIds.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No {label.toLowerCase()} selected yet.
          </Typography>
        ) : (
          <>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {(showAll ? selectedOptions : selectedOptions.slice(0, 5)).map((item) => (
                <Chip
                  key={item.value}
                  label={item.label}
                  onDelete={disabled ? undefined : () => handleChipDelete(item.value)}
                  sx={{
                    backgroundColor: "#e0e0e0", // light gray
                    color: "#4c4c5c", // black text
                    "& .MuiChip-deleteIcon": {
                      color: "#857979",
                      "&:hover": { color: "#000" },
                    },
                  }}
                />
              ))}
            </Box>

            {selectedIds.length > 5 && (
              <Button
                onClick={toggleShowAll}
                size="small"
                sx={{
                  mt: 1,
                  textTransform: "none",
                  color: "#000",
                }}
              >
                {showAll
                  ? "Show Less"
                  : `Show More (${selectedIds.length - 5} more)`}
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default DropdownWithSelectedList;